import { IntegrationType } from '../enums.js'
import type { CompanyIntegrationMetadataVista } from '../types/company-integration.js'
import type { IntegrationMappingsViewpointVista } from '../types/integration.js'

/**
 * The minimum number of days at which we consider a pending integration sync overdue.
 * Should be kept in sync with the copy in the Slack notification from the analytics event.
 */
export const MIN_DAYS_PENDING_SYNC_OVERDUE = 8

// This is a limit imposed by Sage, see https://github.com/siteline/siteline/issues/9034
export const MAX_SAGE_INVOICE_CODE_LENGTH = 15

// This is a limit imposed by Spectrum (per error received when attempting to sync longer #)
export const MAX_SPECTRUM_INVOICE_CODE_LENGTH = 10

// This is a limit imposed by Spectrum (per error received when attempting to sync longer #)
export const MAX_SPECTRUM_BATCH_NUMBER_LENGTH = 10

// This is a limit imposed by Sage (seems to be the max by trial and error)
export const MAX_SAGE_300_DRAW_NUMBER_LENGTH = 15

// The maximum number of characters supported for a Sage Intacct reference number
// the reference number will be cut off after this number of characters
export const MAX_SAGE_INTACCT_REFERENCE_NUMBER_LENGTH = 120

// Based on maxing out the possible length in the Sage Intacct UI
export const MAX_SAGE_INTACCT_BILLING_APPLICATION_NO_LENGTH = 100

// This is a limit imposed by Foundation (per error received when attempting to sync longer #)
export const MAX_FOUNDATION_INVOICE_NUMBER_LENGTH = 10

// This is a limit imposed by CMIC (per experimentation in the CMiC UI)
export const MAX_CMIC_INVOICE_NUMBER_LENGTH = 11

// This is a limit imposed by Vista (per error received when attempting to sync longer #)
export const MAX_VISTA_INVOICE_NUMBER_LENGTH = 10

// This is a limit imposed by Vista. I don't understand exactly how Vista batches work but the input
// field maxes out at 6 characters and must be numeric.
export const MAX_VISTA_BATCH_NUMBER_LENGTH = 6

// This is a limit imposed by QuickBooks Online (found by experimenting with posting invoices)
export const MAX_QUICKBOOKS_ONLINE_INVOICE_NUMBER_LENGTH = 21

// Around this date, we started recording all pay app ERP syncs so that we could surface ERP
// sync statuses in the UI. We opted to use this cut-off date as a reference since we don’t
// know the sync status of pay apps created prior to this date. Pay apps created prior to this
// date are treated as already synced across all surfaces using this variable, meaning a need
// to sync is *not* surfaced for them. This is a safer assumption to prevent incorrect or
// misleading sync statuses from being surfaced.
export const ERP_SYNC_FIELD_INTRODUCED_AT = '2024-09-23T23:00:00Z'

/**
 * Returns whether a Vista pay app sync should write to JB Billing (true) or AR (false).
 * Project-level setting takes precedence over company-level, defaulting to false (AR).
 */
export function shouldWriteToVistaJbBilling(
  mappings: Pick<IntegrationMappingsViewpointVista, 'writeToJbBilling'>,
  companyIntegrationMetadata: Pick<CompanyIntegrationMetadataVista, 'writeToJbBilling'>
): boolean {
  if (mappings.writeToJbBilling !== undefined) {
    return mappings.writeToJbBilling
  }
  if (companyIntegrationMetadata.writeToJbBilling !== undefined) {
    return companyIntegrationMetadata.writeToJbBilling
  }
  return false
}

export function getIntegrationInvoiceCodeMaxLength(
  integrationType: IntegrationType
): number | null {
  switch (integrationType) {
    case IntegrationType.SAGE_100_CONTRACTOR:
    case IntegrationType.SAGE_100_CONTRACTOR_AGAVE:
    case IntegrationType.SAGE_100_CONTRACTOR_PEYOTE:
    case IntegrationType.SAGE_300_CRE:
    case IntegrationType.SAGE_300_CRE_AGAVE:
      return MAX_SAGE_INVOICE_CODE_LENGTH
    case IntegrationType.SPECTRUM_PEYOTE:
    case IntegrationType.SPECTRUM:
      return MAX_SPECTRUM_INVOICE_CODE_LENGTH
    case IntegrationType.FOUNDATION:
    case IntegrationType.FOUNDATION_PEYOTE:
      return MAX_FOUNDATION_INVOICE_NUMBER_LENGTH
    case IntegrationType.VISTA:
    case IntegrationType.VISTA_PEYOTE:
      return MAX_VISTA_INVOICE_NUMBER_LENGTH
    case IntegrationType.QUICKBOOKS_ONLINE:
    case IntegrationType.QUICKBOOKS_ONLINE_PEYOTE:
      return MAX_QUICKBOOKS_ONLINE_INVOICE_NUMBER_LENGTH
    case IntegrationType.ACUMATICA_PEYOTE:
    case IntegrationType.ACUMATICA:
    case IntegrationType.COMPUTER_EASE_FILE:
    case IntegrationType.FOUNDATION_FILE:
    case IntegrationType.GC_PAY:
    case IntegrationType.PROCORE_PEYOTE:
    case IntegrationType.PROCORE:
    case IntegrationType.QUICKBOOKS_ENTERPRISE_FILE:
    case IntegrationType.TEST:
    case IntegrationType.TEXTURA:
    case IntegrationType.SAGE_INTACCT_PEYOTE:
    case IntegrationType.SAGE_INTACCT:
    case IntegrationType.CMIC_PEYOTE:
    case IntegrationType.CMIC:
    case IntegrationType.NETSUITE_FILE:
      return null
  }
}
